/**
 * HostelNewBooking.jsx — CAMP-RES-DEMO-01
 * Multi-step booking wizard: Student → Room/Bed → Rate → Dates → Confirm
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, ArrowRight, User, BedDouble, Star, CalendarCheck,
  Search, CheckCircle2, IndianRupee, Shield, AlertCircle, Building2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VCE_INST = "44444444-4444-4444-4444-444444444444";

const STEPS = ["Student", "Room & Bed", "Rate", "Dates", "Confirm"];

const CATEGORY_LABELS = {
  SUITE:    "Signature Studio / Executive Apt",
  DELUXE:   "Deluxe Single / Premium Twin",
  SHARING:  "Premium Shared Residence",
  AC:       "Accessible Premium Room",
  STANDARD: "Standard Room",
};

export default function HostelNewBooking() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledRoomId = searchParams.get("room_id");

  const institutionId = user?.institution_id || VCE_INST;

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Student
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Step 2: Room & Bed
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);

  // Step 3: Rate
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);

  // Step 4: Dates
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  // Created booking
  const [createdBooking, setCreatedBooking] = useState(null);

  // Load rooms on mount
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/v1/hostel/rooms?institution_id=${institutionId}&status=AVAILABLE`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const roomList = r.data?.rooms || r.data || [];
        setRooms(roomList);
        if (prefilledRoomId) {
          const pre = roomList.find((rm) => rm.id === prefilledRoomId);
          if (pre) setSelectedRoom(pre);
        }
      })
      .catch(() => {});
  }, [token, institutionId, prefilledRoomId]);

  // Load rates
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/v1/hostel/rates?institution_id=${institutionId}&approval_status=APPROVED`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setRates(r.data?.rates || r.data || []))
      .catch(() => {});
  }, [token, institutionId]);

  // Load beds when room selected
  useEffect(() => {
    if (!token || !selectedRoom) return;
    axios
      .get(`${API}/api/v1/hostel/rooms/${selectedRoom.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setBeds(r.data?.beds?.filter((b) => b.status === "AVAILABLE") || []))
      .catch(() => {});
  }, [token, selectedRoom]);

  const searchStudents = async (q) => {
    if (!q || q.length < 2) return;
    setSearchingStudents(true);
    try {
      const r = await axios.get(
        `${API}/api/v1/hostel/students/search?institution_id=${institutionId}&q=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudentResults(r.data?.students || r.data || []);
    } catch (e) {
      setStudentResults([]);
    } finally {
      setSearchingStudents(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!selectedStudent;
    if (step === 1) return !!(selectedRoom && selectedBed);
    if (step === 2) return !!selectedRate;
    if (step === 3) return !!(checkIn && checkOut && checkOut > checkIn);
    return false;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/api/v1/hostel/bookings`,
        {
          student_id: selectedStudent.student_id,
          hostel_id: selectedRoom.hostel_id,
          room_id: selectedRoom.id,
          bed_id: selectedBed.id,
          rate_id: selectedRate.id,
          check_in_date: checkIn,
          check_out_date: checkOut,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreatedBooking(r.data);
      setStep(5); // Success step
      toast.success("Booking created successfully!");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Success step
  if (step === 5 && createdBooking) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center space-y-6" data-testid="booking-success">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Booking Created</h2>
          <p className="text-slate-400 text-sm mt-1">
            Reference: <span className="font-mono text-amber-300">{createdBooking.booking_reference}</span>
          </p>
        </div>
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-left space-y-2">
          <InfoRow label="Student" value={selectedStudent?.display_name || selectedStudent?.id} />
          <InfoRow label="Room" value={`Room ${selectedRoom?.room_number} — ${CATEGORY_LABELS[selectedRoom?.room_category] || selectedRoom?.room_category}`} />
          <InfoRow label="Bed" value={selectedBed?.bed_label} />
          <InfoRow label="Rate" value={selectedRate?.name} />
          <InfoRow label="Period" value={`${checkIn} → ${checkOut}`} />
          <InfoRow label="Total" value={`₹${(createdBooking.total_amount || 0).toLocaleString("en-IN")}`} highlight />
        </div>
        <div className="flex gap-3">
          <Button
            className="flex-1 bg-amber-600 hover:bg-amber-500 text-white"
            onClick={() => navigate(`/campus/hostel/bookings/${createdBooking.id}`)}
            data-testid="btn-view-booking"
          >
            View Booking
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-slate-600 text-slate-300"
            onClick={() => navigate("/campus/hostel/bookings")}
            data-testid="btn-all-bookings"
          >
            All Bookings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="hostel-new-booking">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/campus/hostel/bookings")}
          className="text-slate-400 hover:text-white" data-testid="btn-back-bookings">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-amber-400" />
            <h1 className="text-xl font-bold text-white">New Booking — Kaveri Bhavan</h1>
          </div>
          <p className="text-slate-400 text-sm">Premium residential booking wizard</p>
        </div>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-0" data-testid="booking-steps">
        {STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div className={`flex items-center gap-2 ${i <= step ? "text-amber-300" : "text-slate-500"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${
                i < step ? "bg-amber-600 border-amber-500 text-white" :
                i === step ? "border-amber-400 text-amber-300" :
                "border-slate-600 text-slate-500"
              }`}>
                {i < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className="text-xs hidden sm:block">{label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-amber-600" : "bg-slate-700"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-6 min-h-64">
        {/* Step 0: Student Selection */}
        {step === 0 && (
          <div className="space-y-4" data-testid="step-student">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-amber-400" />
              <h2 className="font-semibold text-white">Select Student</h2>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="student-search-input"
                  placeholder="Search by name or student number…"
                  value={studentQuery}
                  onChange={(e) => setStudentQuery(e.target.value)}
                  className="pl-9 bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <Button
                data-testid="btn-search-student"
                onClick={() => searchStudents(studentQuery)}
                disabled={searchingStudents}
                className="bg-slate-700 hover:bg-slate-600 text-white border border-slate-600"
              >
                {searchingStudents ? "…" : "Search"}
              </Button>
            </div>
            {studentResults.length > 0 && (
              <div className="space-y-2">
                {studentResults.map((s) => (
                  <button
                    key={s.student_id}
                    data-testid={`student-option-${s.student_id}`}
                    onClick={() => { setSelectedStudent(s); setStudentResults([]); }}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                      selectedStudent?.student_id === s.student_id
                        ? "border-amber-500 bg-amber-900/20"
                        : "border-slate-600 bg-slate-700/50 hover:border-slate-400"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-xs text-white font-bold">
                      {(s.display_name || s.student_number || "S")[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{s.display_name || "—"}</p>
                      <p className="text-xs text-slate-400">{s.student_number}</p>
                    </div>
                    {selectedStudent?.student_id === s.student_id && (
                      <CheckCircle2 className="w-4 h-4 text-amber-400 ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedStudent && (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-amber-500/50 bg-amber-900/20" data-testid="selected-student">
                <User className="w-4 h-4 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-white">{selectedStudent.display_name}</p>
                  <p className="text-xs text-slate-400">{selectedStudent.student_number}</p>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-400 ml-auto" />
              </div>
            )}
          </div>
        )}

        {/* Step 1: Room & Bed */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-room">
            <div className="flex items-center gap-2 mb-4">
              <BedDouble className="w-4 h-4 text-amber-400" />
              <h2 className="font-semibold text-white">Select Room & Bed</h2>
            </div>
            <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto pr-1">
              {rooms.map((rm) => (
                <button
                  key={rm.id}
                  data-testid={`room-option-${rm.id}`}
                  onClick={() => { setSelectedRoom(rm); setSelectedBed(null); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                    selectedRoom?.id === rm.id
                      ? "border-amber-500 bg-amber-900/20"
                      : "border-slate-600 bg-slate-700/50 hover:border-slate-400"
                  }`}
                >
                  <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Room {rm.room_number}</p>
                    <p className="text-xs text-slate-400">
                      {CATEGORY_LABELS[rm.room_category] || rm.room_category} · {rm.area_sqft} sqft · Capacity {rm.capacity}
                    </p>
                  </div>
                  {selectedRoom?.id === rm.id && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
                </button>
              ))}
            </div>

            {/* Bed Selection */}
            {selectedRoom && (
              <div>
                <p className="text-xs font-medium text-slate-300 mb-2">
                  Available Beds in Room {selectedRoom.room_number}
                </p>
                <div className="flex flex-wrap gap-2">
                  {beds.length > 0 ? beds.map((bed) => (
                    <button
                      key={bed.id}
                      data-testid={`bed-option-${bed.id}`}
                      onClick={() => setSelectedBed(bed)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        selectedBed?.id === bed.id
                          ? "border-amber-500 bg-amber-900/20 text-amber-300"
                          : "border-slate-600 bg-slate-700/50 text-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {bed.bed_label}
                      {selectedBed?.id === bed.id && <CheckCircle2 className="w-3 h-3 inline ml-1.5 text-amber-400" />}
                    </button>
                  )) : (
                    <p className="text-xs text-slate-500">No available beds in this room.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Rate Selection */}
        {step === 2 && (
          <div className="space-y-3" data-testid="step-rate">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-amber-400" />
              <h2 className="font-semibold text-white">Select Rate</h2>
              {selectedRoom && (
                <Badge className="bg-slate-700 text-slate-300 border-slate-600 text-xs">
                  Showing rates for {CATEGORY_LABELS[selectedRoom.room_category] || selectedRoom.room_category}
                </Badge>
              )}
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {rates
                .filter((r) => !selectedRoom || r.room_category === selectedRoom.room_category)
                .map((rate) => (
                  <button
                    key={rate.id}
                    data-testid={`rate-option-${rate.id}`}
                    onClick={() => setSelectedRate(rate)}
                    className={`w-full flex items-center gap-4 p-3 rounded-lg border transition-all text-left ${
                      selectedRate?.id === rate.id
                        ? "border-amber-500 bg-amber-900/20"
                        : "border-slate-600 bg-slate-700/50 hover:border-slate-400"
                    }`}
                  >
                    <IndianRupee className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white">{rate.name}</p>
                      <p className="text-xs text-slate-400">
                        {rate.billing_period} · {rate.occupancy_type} occupancy
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-amber-300">
                        ₹{(rate.rate_amount || 0).toLocaleString("en-IN")}
                      </p>
                      <p className="text-xs text-slate-400">+ ₹{(rate.security_deposit || 0).toLocaleString("en-IN")} deposit</p>
                    </div>
                    {selectedRate?.id === rate.id && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
                  </button>
                ))}
              {rates.filter((r) => !selectedRoom || r.room_category === selectedRoom.room_category).length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">
                  No approved rates for this room category.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Dates */}
        {step === 3 && (
          <div className="space-y-4" data-testid="step-dates">
            <div className="flex items-center gap-2 mb-4">
              <CalendarCheck className="w-4 h-4 text-amber-400" />
              <h2 className="font-semibold text-white">Booking Dates</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Check-in Date</label>
                <Input
                  data-testid="check-in-date"
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Check-out Date</label>
                <Input
                  data-testid="check-out-date"
                  type="date"
                  value={checkOut}
                  min={checkIn}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>
            {checkIn && checkOut && checkOut <= checkIn && (
              <p className="text-xs text-red-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Check-out must be after check-in
              </p>
            )}
            {checkIn && checkOut && checkOut > checkIn && selectedRate && (
              <div className="bg-slate-700/50 rounded-lg p-3 text-sm">
                <div className="flex justify-between text-slate-300 mb-1">
                  <span>Duration</span>
                  <span>{Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24))} days</span>
                </div>
                <div className="flex justify-between text-slate-300 mb-1">
                  <span>Rate</span>
                  <span>₹{(selectedRate.rate_amount || 0).toLocaleString("en-IN")} / {selectedRate.billing_period?.toLowerCase()}</span>
                </div>
                <div className="flex justify-between text-slate-300 mb-1">
                  <span>Security Deposit</span>
                  <span>₹{(selectedRate.security_deposit || 0).toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between font-bold text-amber-300 border-t border-slate-600 pt-1 mt-1">
                  <span>Est. Total</span>
                  <span>
                    ₹{(
                      (selectedRate.rate_amount || 0) *
                      Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24 * 30.44)) +
                      (selectedRate.security_deposit || 0)
                    ).toLocaleString("en-IN")}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Confirm */}
        {step === 4 && (
          <div className="space-y-4" data-testid="step-confirm">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-4 h-4 text-amber-400" />
              <h2 className="font-semibold text-white">Confirm Booking</h2>
            </div>
            <div className="space-y-2">
              <InfoRow label="Student" value={selectedStudent?.display_name + " · " + selectedStudent?.student_number} />
              <InfoRow label="Room" value={`Room ${selectedRoom?.room_number} — ${CATEGORY_LABELS[selectedRoom?.room_category] || selectedRoom?.room_category}`} />
              <InfoRow label="Bed" value={selectedBed?.bed_label} />
              <InfoRow label="Rate" value={`${selectedRate?.name} — ₹${(selectedRate?.rate_amount || 0).toLocaleString("en-IN")} / ${selectedRate?.billing_period?.toLowerCase()}`} />
              <InfoRow label="Period" value={`${checkIn} → ${checkOut}`} />
              <InfoRow label="Security Deposit" value={`₹${(selectedRate?.security_deposit || 0).toLocaleString("en-IN")}`} />
            </div>
            <p className="text-xs text-slate-500 italic pt-2">
              [DEMO] Illustrative booking — all data is synthetic.
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between" data-testid="step-navigation">
        <Button
          variant="outline"
          onClick={() => step === 0 ? navigate("/campus/hostel/bookings") : setStep(step - 1)}
          className="border-slate-600 text-slate-300 hover:text-white"
          data-testid="btn-prev-step"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step < 4 ? (
          <Button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className="bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
            data-testid="btn-next-step"
          >
            Next <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            data-testid="btn-submit-booking"
          >
            {submitting ? "Creating…" : "Create Booking"}
            <CheckCircle2 className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-slate-700/40">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`text-sm font-medium ${highlight ? "text-amber-300" : "text-white"}`}>{value}</span>
    </div>
  );
}
